import SelectField from "$components/inputs/SelectField";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { MAX_AGE_IN_YEARS_FOR_CHILD_ACCOUNT } from "$routes/patient/[:patientId]/_lib/is-child-patient";
import { gql } from "@apollo/client";
import { alpha, styled, Typography } from "@material-ui/core";
import {
  differenceInYears,
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
} from "date-fns";
import { type FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { PAGE_SIZE } from "../_lib/constants";
import { useChildImmunisationRegisterLazyQuery } from "./__generated__/page.gql";
import {
  mapGqlPatientToVaccinationDates,
  type RegisterRow,
} from "./_lib/mapper";
import { ChildImmunisationRegisterTable } from "./_lib/register-table";

gql`
query ChildImmunisationRegister($input: ChildImmunisationRegisterInput!) {
  childImmunisationRegister(input: $input) {
    records {
      patient {
        id
        guardians {
          phone {
            number
          }
        }
        person {
          name {
            fullname
          }
          dob
          gender
          addresses {
            street
            ward
            subCounty
            state
            country
          }
        }
        meta {
          created
        }
      }
      bcg
      hepatitisB0
      opv0
      penta1
      pcv1
      rota1
      opv1
      ipv1
      penta2
      pcv2
      rota2
      opv2
      penta3
      pcv3
      rota3
      opv3
      ipv2
      vitaminA1
      vitaminA2
      measles1
      yellowFever
      meningitisA
      measles2
      hpv
    }
  }
}`;

// Larger batch size for client-side pagination
const FETCH_SIZE = 500;

const HeaderContainer = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const MonthPickerContainer = styled("div")(({ theme }) => ({
  maxWidth: "400px",
  marginBottom: theme.spacing(3),
}));

const Legend = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const LegendDot = styled("div")(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: "50%",
  backgroundColor: alpha(theme.palette.brand.main, 0.1),
}));

const LegendText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

const MonthPickerHeader: FC<{
  selectedMonth: string;
  setSelectedMonth: (month: string) => void;
  setCurrentPage: (page: number) => void;
  total: number;
}> = ({ selectedMonth, setSelectedMonth, setCurrentPage }) => {
  const months = Array.from({ length: 6 }, (_, i) => {
    const date = subMonths(new Date(), i);
    return {
      value: format(date, "yyyy-MM"),
      label: format(date, "MMMM"),
    };
  });

  return (
    <div>
      <HeaderContainer>
        <Typography variant="h4">Child immunization register</Typography>
      </HeaderContainer>
      <MonthPickerContainer>
        <SelectField
          label="Vaccinations completed during"
          name="month-select"
          options={months}
          value={selectedMonth}
          onChange={(e) => {
            setSelectedMonth(e.target.value as string);
            setCurrentPage(0);
          }}
          fullWidth
        />
      </MonthPickerContainer>
      <Legend>
        <LegendDot />
        <LegendText variant="body2">
          <FormattedMessage
            id="childImmunisationRegister.legend.text"
            defaultMessage="Vaccinations completed in selected month"
          />
        </LegendText>
      </Legend>
    </div>
  );
};

export const ChildImmunisationRegisterPage: FC = () => {
  const { formatMessage } = useIntl();

  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get("month") ?? "";
  const currentPageParam = Number(searchParams.get("currentPage"));

  const [currentPage, setCurrentPage] = useState(
    !Number.isNaN(currentPageParam) ? currentPageParam : 0,
  );

  const [selectedMonth, setSelectedMonth] = useState(
    monthParam || format(new Date(), "yyyy-MM"),
  );

  const { facilityId } = useUser();

  const [fetchRecords, { data, loading, error }] =
    useChildImmunisationRegisterLazyQuery();

  useEffect(() => {
    if (selectedMonth && facilityId) {
      const startDate = startOfMonth(new Date(selectedMonth));
      const endDate =
        new Date(selectedMonth) > new Date()
          ? subMonths(new Date(), 1)
          : endOfMonth(new Date(selectedMonth));

      fetchRecords({
        variables: {
          input: {
            filterOptions: {
              startDate: format(startDate, "yyyy-MM-dd"),
              endDate: format(endDate, "yyyy-MM-dd"),
            },
            paginationOptions: {
              offset: 0, // Always start from the beginning to fetch the whole batch
              limit: FETCH_SIZE, // Fetch a larger number of records for client-side pagination
            },
          },
        },
      });
    }
  }, [selectedMonth, facilityId, fetchRecords]);

  useEffect(() => {
    setSearchParams({ month: selectedMonth, currentPage: `${currentPage}` });
  }, [selectedMonth, currentPage, setSearchParams]);

  if (error) {
    toast.error(
      formatMessage({
        id: "childImmunisationRegister.error.fetch",
        defaultMessage: "Failed to fetch immunisation records",
      }),
    );
  }

  const patientVaccinationDatesRecords = (
    data?.childImmunisationRegister?.records ?? []
  ).map(mapGqlPatientToVaccinationDates);

  const now = new Date();
  const maxAgeInYears = MAX_AGE_IN_YEARS_FOR_CHILD_ACCOUNT;

  const patientVaccinationRecords = useMemo(() => {
    return patientVaccinationDatesRecords
      .reduce<RegisterRow[]>((acc, record) => {
        if (!record.hasAtLeastOneVaccination) return acc;
        const dob = new Date(record.result.dateOfBirth);
        if (differenceInYears(now, dob) <= maxAgeInYears) {
          acc.push(record.result);
        }
        return acc;
      }, [])
      .sort((a, b) => {
        const aTime = new Date(a.dateOfBirth).getTime();
        const bTime = new Date(b.dateOfBirth).getTime();
        return aTime - bTime;
      });
  }, [patientVaccinationDatesRecords, now, maxAgeInYears]);

  const paginatedRecords = useMemo(() => {
    return patientVaccinationRecords.slice(
      currentPage * PAGE_SIZE,
      (currentPage + 1) * PAGE_SIZE,
    );
  }, [patientVaccinationRecords, currentPage]);

  return (
    <div>
      <MonthPickerHeader
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        setCurrentPage={setCurrentPage}
        total={patientVaccinationRecords.length}
      />
      <ChildImmunisationRegisterTable
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        rows={paginatedRecords}
        selectedMonth={selectedMonth}
        totalRows={patientVaccinationRecords.length}
      />
    </div>
  );
};
